import { useCallback } from 'react';
import { INewUniprofileResume, IBasicInfoStep } from 'types/newUniprofile';
import stepPercentageStore from 'store/UniprofileResume/stepPercentage';
import { get } from 'lodash';

type PercentageData = {
    percent: number;
    unfinished?: string[];
};

const calculateBasicInfoPercentage = (basicInfo: IBasicInfoStep) => {
    const requiredFields = ['firstName', 'lastName', 'npi', 'credential', 'gender', 'email', 'tel'] as const;
    const totalFields = requiredFields.length;
    const filledFields = requiredFields.filter((field) => {
        const value = basicInfo[field];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    }).length;

    return filledFields / totalFields;
};

const calculateLicensePercentage = (_license: INewUniprofileResume['license']) => {
    const requiredFields = ['identification.type', 'identification.frontUrl'];
    const totalFields = requiredFields.length;
    const filledFields = requiredFields.filter((field) => {
        const value = get(_license, field);
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    }).length;
    return filledFields / totalFields;
};

const calculateInsurancePercentage = (insurance: INewUniprofileResume['insurance']) => {
    const requiredFields = ['supportPayType'];
    const totalFields = requiredFields.length;
    const filledFields = requiredFields.filter((field) => {
        const value = get(insurance, field);
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    }).length;
    return filledFields / totalFields;
};

const calculateProfessionalPercentage = (_professional: INewUniprofileResume['professional']) => {
    const requiredFields = ['yearExp', 'headLine', 'intro', 'photo'];
    const totalFields = requiredFields.length;
    const filledFields = requiredFields.filter((field) => {
        const value = get(_professional, field);
        return !!value;
    }).length;
    return filledFields / totalFields;
};

const useSetNewPercentages = () => {
    const [, setPercentageStore] = stepPercentageStore.useStore();

    const setPercentage = useCallback((resumeData: INewUniprofileResume) => {
        const percentageArray: Record<string, PercentageData>[] = [
            {
                basicInfo: {
                    percent: calculateBasicInfoPercentage(resumeData.basicInfo),
                },
            },
            {
                license: {
                    percent: calculateLicensePercentage(resumeData.license),
                },
            },
            {
                insurance: {
                    percent: calculateInsurancePercentage(resumeData.insurance),
                },
            },
            {
                professional: {
                    percent: calculateProfessionalPercentage(resumeData.professional),
                },
            },
        ];

        const percentageStoreData = percentageArray.reduce((r, c) => {
            const [k, v] = Object.entries(c)[0];
            r[k] = v;
            return r;
        }, {} as Record<string, PercentageData>);

        setPercentageStore('data', percentageStoreData as any);
    }, [setPercentageStore]);

    return [setPercentage];
};

export default useSetNewPercentages;
